<template>
  <div ref="listRef" class="template-wrapper" @scroll="onScroll">
    <div class="container">
      <div class="sticky top-0 z-[11] bg-back-1 pt-[30px] -mx-[60px] px-[60px] filter-box">
        <a-breadcrumb class="mb-[20px]" :separator="separator">
          <a-breadcrumb-item class="text-font-60">
            <nuxt-link to="/home">{{ $t('shou-ye') }}</nuxt-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item v-if="isChinese()">
            <nuxt-link to="/template">{{ $t('mo-ban-zhong-xin') }}</nuxt-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item> {{ currentTag?.tag_name }}</a-breadcrumb-item>
        </a-breadcrumb>
        <div class="w-full flex-y-center pb-[5px]">
          <DictFilter
            v-model="searchFrom.sort"
            name="sorts"
            :has-all="false"
            class="text-14 gap-x-[20px]"
          />
          <div class="justify-end flex-1 flex-y-center">
            <div class="flex-y-center">
              <span class="dict-name line">{{ $t('ban-shi') }}</span>
              <DictFilter
                v-model="searchFrom.format"
                name="formats"
                class="format text-14 gap-x-[20px]"
                @change="onChangeFormat"
              />
            </div>
            <div class="flex-y-center ml-[30px]">
              <span class="dict-name -mr-[15px]">
                {{ $t('yan-bo-shi-lei-xing') }}
              </span>
              <a-select
                v-model:value="searchFrom.map_types"
                :placeholder="$t('qing-xuan-ze')"
                size="small"
                class="w-[120px] dict-select"
                allow-clear
                :options="studioListFilter"
                @change="onChangeStudio"
              >
                <template #suffixIcon>
                  <iconpark-icon name="arrowdown" class="opacity-30" />
                </template>
              </a-select>
            </div>
          </div>
        </div>
      </div>
      <a-alert
        v-if="sceneTip"
        :message="sceneTip"
        type="info"
        show-icon
        closable
        :after-close="onCloseEnd"
      />

      <div class="py-[15px]">
        <TemplateMasonry
          v-if="calList.length"
          :break-points="{
            '0': 4,
            '1500': 5,
          }"
          :list="calList"
          class="gap-y-[0px]"
          :template_source="TEMPLATE_SOURCE.TEMPLATE_PAGE"
        />
        <EmptyView v-else-if="!pending && !first" />
        <Divider v-if="list.length === total && total >= 15" />
      </div>
      <div
        v-if="pending"
        :class="{
          'fixed h-[calc(100%-90px)]': current === 1,
        }"
        class="w-full flex-center mt-[20px] bottom-0 left-0 z-8 bg-back-1 bg-opacity-60"
      >
        <a-spin />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { isChinese } from '@/utils/area';
import { TEMPLATE_SOURCE } from '@/utils/track';
import { debounce } from 'lodash-es';
import { getStudioList, getTemplate, getTemplateList } from '~/api';
import type { TemplateParams } from '~/api/interface';
import TemplateMasonry from '~/components/template-masonry/index.vue';
import { TEMPLATE_SORT } from '~/constants';
import { useTemplateDetail } from '~/hooks/useModal/modals/template';
import { useMountedWatch } from '~/hooks/useMountedWatch';
import { SCROLL_LIST, useScrollList } from '~/hooks/useScrollList';
import { useWatchPageSeoData } from '~/hooks/useSeo';
import { useStarPatchList } from '~/hooks/useStarPatch';

const separator = h('iconpark-icon', { name: 'breadcrumb-separator', size: 10 });

const route = useRoute();
const { $t } = useNuxtApp();

const searchFrom = reactive<any>({
  sort: TEMPLATE_SORT.ALL,
  format: 'all',
  map_types: route.query.map_types || undefined,
  tag_ids: decodeTagUrlPath(route.params.tag as string),
});

const tagMap = useTagMap();

const currentTag = computed(() => {
  return (tagMap.value as any)[searchFrom.tag_ids];
});

const listRef = ref();
const studioList = ref<any[]>([]);
const sceneTip = ref('');
const KEYNAME = 'youyan-studio-keys';
const tagsIsSmall = ref(false);

const studioListFilter = computed(() => {
  return studioList.value.filter(x => {
    const l = x.resolutions;
    if (searchFrom.format === 'all' || !l || l.length === 0) {
      return true;
    }

    return l.includes(searchFrom.format);
  });
});

const seoData = computed(() => {
  const tag = currentTag.value?.tag_name;
  return {
    seo_title: $t('tag-shi-pin-zhi-zuo-tag-xu-ni-ren-shi-pin-you-yan-ai-shi-pin', [tag, tag]),
    seo_description: $t(
      'you-yan-shi-pin-zhi-zuo-ping-tai-zhuan-ye-da-zao-tag-shi-pin-tag-xu-ni-ren-shi-pin-yi-ji-ai-shi-pin-zhi-zuo-chuang-xin-tag-fang-shi-man-zu-nin-de-duo-yang-hua-tag-xu-qiu',
      [tag, tag, tag, tag],
    ),
    seo_keywords: tag,
  };
});

useWatchPageSeoData(seoData);

const resourceLocation = injectResourceLocation();

const { list, pending, refresh, next, total, current } = useScrollList(
  async params => {
    const p: TemplateParams = {
      ...params,
      tag_ids: searchFrom.tag_ids,
      o: searchFrom.sort,
      is_template_center_template: 1,
      map_types: searchFrom.map_types,
      resolution: searchFrom.format !== 'all' ? searchFrom.format : undefined,
      platform: 0,
      online_status: 0,
      query_star: import.meta.client,
    };

    const res = await getTemplateList(p);
    res.data?.results?.forEach(item => {
      if (import.meta.server) {
        item.query_star = false; // 标记这些数据没有查询过star
      }
    });
    return res;
  },
  {
    key: SCROLL_LIST.TEMPLATE_LIST,
  },
);

const { calList, status } = useStarPatchList(list, 2);

let first = true;

useMountedWatch([calList, status], async () => {
  if (first && status.value === 'success' && route.query.id) {
    first = false;
    const index = calList.value.findIndex((x: any) => x.id === Number(route.query.id)) || -1;
    if (index > -1) {
      setTimeout(() => {
        listRef.value.querySelector(`.card-item:nth-child(${index + 1})`)?.click();
      }, 100);
    } else {
      const details = await getTemplate({
        template_id: Number(route.query.id),
      });
      if (details.code === 0) {
        useTemplateDetail(
          [details.data!],
          details.data!,
          {
            templateSource: TEMPLATE_SOURCE.TEMPLATE_PAGE,
            source: resourceLocation?.source,
          },
          undefined,
        );
      }
    }
  }
});

useBackToTop(() => ({
  scrollContainer: '.template-wrapper',
  item: '.card-item',
  total: total.value,
}));

const onScroll = (e: any) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target as HTMLBRElement;
  if (scrollTop > 1 && !tagsIsSmall.value) {
    tagsIsSmall.value = true;
  } else if (scrollTop <= 1 && tagsIsSmall.value) {
    tagsIsSmall.value = false;
  }

  if (Math.round(scrollTop + clientHeight) >= scrollHeight - 1) {
    next();
  }
};

const getStudioData = async () => {
  const res = await getStudioList();
  if (res.code !== 0) {
    return;
  }

  studioList.value = !res.data
    ? []
    : res.data.map((item: { resolutions: any; message: any; name: any; id: any }) => ({
        value: String(item.id),
        label: item.name,
        message: item.message,
        resolutions: item.resolutions,
      }));
};

getStudioData();

const onChangeStudio: any = (v: string, d: any) => {
  const keys = JSON.parse(localStorage.getItem(KEYNAME) || '{}');
  sceneTip.value = '';
  if (v && !keys[v] && d.message) {
    sceneTip.value = `${d.label}：“${d.message}”`;
  }
};
const onCloseEnd = () => {
  sceneTip.value = '';
  const keys = JSON.parse(localStorage.getItem(KEYNAME) || '{}');
  localStorage.setItem(KEYNAME, JSON.stringify({ ...keys, [searchFrom.map_types]: true }));
};
const onChangeFormat = () => {
  searchFrom.map_types = undefined;
  sceneTip.value = '';
};
watch(
  searchFrom,
  debounce(() => {
    if (listRef.value) {
      listRef.value.scrollTop = 0;
    }

    refresh();
  }, 500),
);
</script>

<style scoped lang="scss">
.sorts {
  @apply font-semibold;
}

.dict-name {
  @apply text-14 flex-y-center;

  &:after {
    @apply ml-[10px] mr-[20px] w-[1px] h-[12px] block;
    content: '';
  }

  &.line:after {
    @apply bg-back-6;
  }
}

.tag-list {
  @apply w-full flex gap-[20px] flex-wrap mb-[20px] flex-col;
  &.small {
    @apply flex-row;
    :deep(.dict-item) {
      display: none;
      &.active {
        display: flex;
      }
    }
  }
}
.filter-box {
  &:hover {
    .tag-list.small {
      @apply flex-col;
      :deep(.dict-item) {
        display: flex;
      }
    }
  }
}

.template-wrapper {
  @apply flex flex-col overflow-y-scroll relative w-full;
  height: calc(100vh - 60px);

  .container {
    @apply p-[0_160px_0];
    margin: 0 auto;
  }
}

.dict-select {
  :deep(.ant-select-selector) {
    height: 28px !important;
    border-radius: 6px;
    .ant-select-selection-placeholder {
      line-height: 25px;
      color: var(--text-1-40);
    }
    .ant-select-selection-item {
      line-height: 25px;
    }
    // @apply bg-back-3 border-0;
  }
}
</style>
